import React, { useCallback, useMemo, useState } from "react";
import { AlertTriangle, Loader2, Sparkles, ThumbsDown, ThumbsUp } from "lucide-react";

import { aiApi, type EvaluateArgumentRequest, type EvaluateArgumentResponse } from "lib/api/ai";
import { getAiUiStrings, resolveAiLocale } from "i18n/aiUi";

interface AiArgumentEvaluationPanelProps {
  claim: string;
  draftArgument: string;
  issueId?: number;
  onApplyRevision: (value: string) => void;
  parentArgumentId?: number | null;
  stance: "agree" | "disagree";
  teabateId?: number;
}

type FeedbackState = -1 | 1 | null;

function getStrengthLabel(ui: ReturnType<typeof getAiUiStrings>, value: EvaluateArgumentResponse["evaluation"]["overallStrength"]): string {
  switch (value) {
    case "strong":
      return ui.strongStrengthLabel;
    case "weak":
      return ui.weakStrengthLabel;
    case "moderate":
    default:
      return ui.moderateStrengthLabel;
  }
}

const AiArgumentEvaluationPanel: React.FC<AiArgumentEvaluationPanelProps> = ({
  claim,
  draftArgument,
  issueId,
  onApplyRevision,
  parentArgumentId,
  stance,
  teabateId,
}) => {
  const ui = useMemo(() => getAiUiStrings(resolveAiLocale()), []);
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmittingFeedback, setIsSubmittingFeedback] = useState(false);
  const [feedbackState, setFeedbackState] = useState<FeedbackState>(null);
  const [error, setError] = useState<string | null>(null);
  const [response, setResponse] = useState<EvaluateArgumentResponse | null>(null);

  const handleEvaluate = useCallback(async () => {
    const trimmedClaim = claim.trim();
    const trimmedArgument = draftArgument.trim();
    if (!trimmedArgument || (!trimmedClaim && !teabateId)) {
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const payload: EvaluateArgumentRequest = {
        claim: trimmedClaim || undefined,
        draftArgument: trimmedArgument,
        issueId,
        parentArgumentId: parentArgumentId ?? null,
        stance,
        teabateId,
      };
      const nextResponse = await aiApi.evaluateArgument(payload);
      setResponse(nextResponse);
    } catch (caughtError: unknown) {
      const fallbackMessage = ui.searchSuggestionsError;
      const maybeMessage = typeof caughtError === "object" && caughtError && "response" in caughtError
        ? String(((caughtError as { response?: { data?: { message?: string } } }).response?.data?.message) || fallbackMessage)
        : fallbackMessage;
      setError(maybeMessage);
    } finally {
      setIsLoading(false);
    }
  }, [claim, draftArgument, issueId, parentArgumentId, stance, teabateId, ui.searchSuggestionsError]);

  const handleFeedback = useCallback(async (rating: -1 | 1) => {
    if (!response?.meta.invocationId || isSubmittingFeedback) {
      return;
    }

    setIsSubmittingFeedback(true);
    try {
      await aiApi.submitFeedback({
        invocationId: response.meta.invocationId,
        rating,
      });
      setFeedbackState(rating);
    } finally {
      setIsSubmittingFeedback(false);
    }
  }, [isSubmittingFeedback, response?.meta.invocationId]);

  if (!draftArgument.trim()) {
    return null;
  }

  return (
    <section className="rounded-2xl border border-eoe-border-main bg-eoe-bg-hover p-4 dark:border-eoe-dark-border-main dark:bg-eoe-dark-bg-hover">
      <div className="flex flex-col items-start gap-3">
        <div>
          <p className="text-sm font-semibold text-eoe-text dark:text-eoe-dark-text">
            {ui.evaluationTitle}
          </p>
          {response?.meta.awardCost ? (
            <p className="mt-1 text-xs text-eoe-text-sub dark:text-eoe-dark-text-sub">
              {ui.thisCostsAwards(response.meta.awardCost)}
            </p>
          ) : null}
        </div>
        <button
          type="button"
          onClick={() => void handleEvaluate()}
          disabled={isLoading}
          className={`inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-semibold ${
            isLoading ? "btn-primary-disabled opacity-60 cursor-not-allowed" : "btn-secondary"
          }`}
        >
          {isLoading ? <Loader2 className="size-4 animate-spin" aria-hidden /> : <Sparkles className="size-4" aria-hidden />}
          {isLoading ? ui.evaluateArgumentLoading : ui.evaluateArgumentAction}
        </button>
      </div>

      {error ? (
        <p className="mt-3 text-sm text-warning-red dark:text-dark-warning-red">
          {error}
        </p>
      ) : null}

      {response ? (
        <div className="mt-4 space-y-4">
          <div className="flex items-center gap-2 text-sm">
            <span className="font-semibold text-eoe-text dark:text-eoe-dark-text">
              {ui.strengthLabel}:
            </span>
            <span className="rounded-full bg-eoe-blue/10 px-2 py-1 text-xs font-semibold text-eoe-blue dark:bg-eoe-blue/20">
              {getStrengthLabel(ui, response.evaluation.overallStrength)}
            </span>
          </div>

          {response.evaluation.weakPoints.length ? (
            <div>
              <p className="text-sm font-semibold text-eoe-text dark:text-eoe-dark-text">
                {ui.possibleWeakPointsLabel}
              </p>
              <ul className="mt-2 space-y-2 text-sm text-eoe-text-sub dark:text-eoe-dark-text-sub">
                {response.evaluation.weakPoints.map((weakPoint) => (
                  <li key={weakPoint} className="flex gap-2">
                    <AlertTriangle className="mt-0.5 size-4 text-warning-yellow" aria-hidden />
                    <span>{weakPoint}</span>
                  </li>
                ))}
              </ul>
            </div>
          ) : null}

          {response.evaluation.fallacies.length ? (
            <div>
              <p className="text-sm font-semibold text-eoe-text dark:text-eoe-dark-text">
                {ui.possibleFallaciesLabel}
              </p>
              <div className="mt-2 space-y-2">
                {response.evaluation.fallacies.map((fallacy) => (
                  <div
                    key={`${fallacy.type}-${fallacy.explanation}`}
                    className="rounded-xl border border-eoe-border-main bg-white/70 p-3 dark:border-eoe-dark-border-main dark:bg-black/10"
                  >
                    <p className="text-sm font-semibold text-eoe-text dark:text-eoe-dark-text">
                      {fallacy.type}
                    </p>
                    <p className="mt-1 text-sm text-eoe-text-sub dark:text-eoe-dark-text-sub">
                      {fallacy.explanation}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          ) : null}

          <div>
            <p className="text-sm font-semibold text-eoe-text dark:text-eoe-dark-text">
              {ui.suggestedRevisionLabel}
            </p>
            <div className="mt-2 rounded-xl border border-eoe-border-main bg-white/70 p-3 dark:border-eoe-dark-border-main dark:bg-black/10">
              {response.evaluation.suggestedRevision ? (
                <>
                  <p className="text-sm text-eoe-text dark:text-eoe-dark-text">
                    {response.evaluation.suggestedRevision}
                  </p>
                  <button
                    type="button"
                    onClick={() => {
                      if (response.evaluation.suggestedRevision) {
                        onApplyRevision(response.evaluation.suggestedRevision);
                      }
                    }}
                    className="btn-primary mt-3 px-4 py-2 text-sm"
                  >
                    {ui.applyRevisionAction}
                  </button>
                </>
              ) : (
                <p className="text-sm italic text-eoe-text-sub dark:text-eoe-dark-text-sub">
                  {ui.noRevisionAvailable}
                </p>
              )}
            </div>
          </div>

          {response.evaluation.termsNeedingDefinition.length ? (
            <div>
              <p className="text-sm font-semibold text-eoe-text dark:text-eoe-dark-text">
                {ui.termsNeedingDefinitionLabel}
              </p>
              <div className="mt-2 flex flex-wrap gap-2">
                {response.evaluation.termsNeedingDefinition.map((term) => (
                  <span
                    key={term}
                    className="rounded-full border border-eoe-border-main px-3 py-1 text-xs text-eoe-text-sub dark:border-eoe-dark-border-main dark:text-eoe-dark-text-sub"
                  >
                    {term}
                  </span>
                ))}
              </div>
            </div>
          ) : null}

          {response.sources.length ? (
            <div>
              <p className="text-sm font-semibold text-eoe-text dark:text-eoe-dark-text">
                {ui.sourceLabel}
              </p>
              <div className="mt-2 flex flex-wrap gap-2">
                {response.sources.map((source) => (
                  <a
                    key={source.url}
                    href={source.url}
                    target="_blank"
                    rel="noreferrer"
                    className="rounded-full border border-eoe-border-main px-3 py-1 text-xs text-eoe-text-sub hover:text-eoe-text dark:border-eoe-dark-border-main dark:text-eoe-dark-text-sub dark:hover:text-eoe-dark-text"
                  >
                    {source.title || source.publisher || source.url}
                  </a>
                ))}
              </div>
            </div>
          ) : null}

          {response.counterpoints.length ? (
            <div>
              <p className="text-sm font-semibold text-eoe-text dark:text-eoe-dark-text">
                {ui.counterpointsLabel}
              </p>
              <div className="mt-2 space-y-2">
                {response.counterpoints.map((counterpoint) => (
                  <div
                    key={counterpoint.summary}
                    className="rounded-xl border border-eoe-border-main bg-white/70 p-3 dark:border-eoe-dark-border-main dark:bg-black/10"
                  >
                    <p className="text-sm text-eoe-text dark:text-eoe-dark-text">
                      {counterpoint.summary}
                    </p>
                    {counterpoint.sources.length ? (
                      <div className="mt-2 flex flex-wrap gap-2">
                        {counterpoint.sources.map((source) => (
                          <a
                            key={source.url}
                            href={source.url}
                            target="_blank"
                            rel="noreferrer"
                            className="rounded-full border border-eoe-border-main px-3 py-1 text-xs text-eoe-text-sub hover:text-eoe-text dark:border-eoe-dark-border-main dark:text-eoe-dark-text-sub dark:hover:text-eoe-dark-text"
                          >
                            {source.title || source.publisher || source.url}
                          </a>
                        ))}
                      </div>
                    ) : null}
                  </div>
                ))}
              </div>
            </div>
          ) : null}

          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => void handleFeedback(1)}
              disabled={isSubmittingFeedback}
              className={`inline-flex items-center gap-2 rounded-full px-3 py-1 text-xs ${
                feedbackState === 1
                  ? "bg-green-600 text-white"
                  : "border border-eoe-border-main text-eoe-text-sub dark:border-eoe-dark-border-main dark:text-eoe-dark-text-sub"
              }`}
            >
              <ThumbsUp className="size-3.5" aria-hidden />
              {ui.feedbackPositive}
            </button>
            <button
              type="button"
              onClick={() => void handleFeedback(-1)}
              disabled={isSubmittingFeedback}
              className={`inline-flex items-center gap-2 rounded-full px-3 py-1 text-xs ${
                feedbackState === -1
                  ? "bg-red-600 text-white"
                  : "border border-eoe-border-main text-eoe-text-sub dark:border-eoe-dark-border-main dark:text-eoe-dark-text-sub"
              }`}
            >
              <ThumbsDown className="size-3.5" aria-hidden />
              {ui.feedbackNegative}
            </button>
            {feedbackState ? (
              <span className="text-xs text-eoe-text-sub dark:text-eoe-dark-text-sub">
                {ui.feedbackThanks}
              </span>
            ) : null}
          </div>

          <p className="text-xs text-eoe-text-sub dark:text-eoe-dark-text-sub">
            {ui.aiDisclaimer}
          </p>
        </div>
      ) : null}
    </section>
  );
};

export default AiArgumentEvaluationPanel;
